import { useApolloClient } from '@apollo/client';
import { CircularProgress } from '@mui/material';
import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import { useEffect, useRef, useState } from 'react';

import { AgButton, AgCheckbox, AgInput } from 'src/components/ui';
import type { GridFilterOption, GridFilterValue } from 'src/modules/grids/DataGrid';

import type { FilterItemProps } from './filterItemTypes';

export default function SelectFilter({ updateFilter, filter, column, onClose }: FilterItemProps) {
  const client = useApolloClient();
  const { getFilterSelectOptions, customFilterType } = column;
  const colName = column.headerName ?? column.field;
  const [options, setOptions] = useState<GridFilterOption[]>([]);
  const [search, setSearch] = useState('');
  const [filteredOptions, setFilteredOptions] = useState<GridFilterOption[]>();
  const filterMethod = customFilterType === 'string_array' ? 'hasSome' : 'in';
  const [selected, setSelected] = useState<GridFilterValue[]>(filter?.where?.[0]?.[filterMethod] ?? []);
  const areOptionsAsync = typeof getFilterSelectOptions === 'function';
  const [loading, setLoading] = useState(true);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function loadOptions() {
      try {
        const res = areOptionsAsync ? await getFilterSelectOptions(client) : (getFilterSelectOptions ?? []);
        const opts = res.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
        setOptions(opts);
        setFilteredOptions(opts);
      } catch {
        setOptions([]);
        setFilteredOptions([]);
      } finally {
        setLoading(false);
      }
    }
    loadOptions();
  }, [getFilterSelectOptions, areOptionsAsync, client]);

  const toggleSelectedOption = (value: GridFilterValue) => {
    setSelected((prev) => {
      if (prev.includes(value)) {
        return prev.filter((v) => v !== value);
      }
      return [...prev, value];
    });
  };

  useEffect(() => {
    setFilteredOptions(
      search ? options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase())) : options
    );
    setFocusedIndex(-1);
  }, [options, search]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex < 0 || !listRef.current) return;
    const item = listRef.current.children[focusedIndex] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  return (
    <>
      {loading ? (
        <div className="flex justify-center items-center py-2">
          <CircularProgress size={24} />
        </div>
      ) : !options.length ? (
        <div className="p-4 text-center text-neutral-9">No options available</div>
      ) : (
        <>
          <div className="p-2">
            <AgInput
              size="sm"
              placeholder="Search"
              aria-label={`Search ${colName} options`}
              value={search}
              onChange={(val) => setSearch(val)}
              startContent={<RiSearchLine size={16} />}
              endContent={
                search ? (
                  <AgButton
                    variant="ghost"
                    size="icon-sm"
                    shape="circle"
                    aria-label="Clear search"
                    onPress={() => setSearch('')}
                  >
                    <RiCloseLine size={16} />
                  </AgButton>
                ) : null
              }
              onKeyDown={(e) => {
                if (!filteredOptions?.length) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setFocusedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setFocusedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
                } else if ((e.key === ' ' || e.key === 'Enter') && focusedIndex >= 0) {
                  e.preventDefault();
                  toggleSelectedOption(filteredOptions[focusedIndex].value);
                }
              }}
            />
          </div>
          <div ref={listRef} className="overflow-y-auto grow flex flex-col">
            {filteredOptions?.map((option, index) => (
              <AgCheckbox
                key={String(option.value)}
                className={`py-1.5 px-2 hover:bg-brand-1 ${index === focusedIndex ? 'bg-brand-1' : ''}`}
                isSelected={selected.includes(option.value)}
                label={option.label}
                onChange={() => toggleSelectedOption(option.value)}
                size="sm"
              />
            ))}
          </div>
        </>
      )}

      <div className="flex justify-between gap-2 p-2 border-t border-neutral-5">
        <AgButton variant="destructive-white" onPress={() => updateFilter({ field: filter.field, where: [] })}>
          Clear
        </AgButton>
        <AgButton
          isDisabled={!selected.length}
          variant="primary"
          onPress={() => {
            updateFilter({ field: filter.field, where: [{ [filterMethod]: selected }] });
            onClose?.();
          }}
        >
          Apply
        </AgButton>
      </div>
    </>
  );
}
